import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { InfoTooltip } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SectionSwitcher } from './SectionSwitcher';
import { visibleNavigation, canLeaveSectionFor } from './navigation.config';
import { usePermissionCheckers } from '@/hooks/usePermission';
import { useUiStore } from '@/stores/ui.store';
import { useActiveModules, useActiveSectionSlug, useSections } from '@/features/sections/sections.queries';
import { useAuth } from '@/features/auth/AuthContext';
import { cn } from '@/lib/utils';

const APP_VERSION = '0.1.0';

/**
 * The sidebar (docs/04 §1): section switcher pinned at the top, permission-filtered
 * groups, active item marked with a left accent bar in the section colour, footer with
 * the app version.
 *
 * Collapsing to icons keeps the labels available as tooltips — an icon rail with no
 * names is unusable for anyone who does not already know the app.
 */
export const SidebarContent = ({ onNavigate }) => {
  const { t } = useTranslation(['nav', 'common']);
  const checkers = usePermissionCheckers();

  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const collapsedGroups = useUiStore((state) => state.collapsedNavGroups);
  const toggleNavGroup = useUiStore((state) => state.toggleNavGroup);

  /*
   * Three filters now, and each answers a different question (docs/03 §2a):
   *
   *   permission  — may *this user* do it?
   *   module      — does *this section* run it?
   *   slug        — is this menu *this business line's* at all?
   *
   * The third is what gives Real Estate and Agency a vertical of their own: neither owns a
   * module the menu could key off, and the section's identity is not a thing configuration
   * can switch off (docs/10 §4.2).
   */
  const modules = useActiveModules();
  const sectionSlug = useActiveSectionSlug();

  /*
   * The sections this user can actually reach — `/sections` is scoped, so the list is
   * already theirs. Feeds the per-section report rows (docs/10 §16).
   *
   * **`null` until the query has answered**, which is not the same as an empty list and has
   * to stay distinguishable from it: `isItemVisible` reads null as "not known yet" and
   * shows every row, and an empty array as "this user reaches no section" and hides them
   * all. Defaulting the destructure to `[]` would have collapsed the two, and every section
   * report would have blinked out of the menu on each cold load.
   */
  const { data: reachableSections } = useSections();
  const sectionSlugs = useMemo(
    () => (reachableSections ? reachableSections.map((row) => row.slug) : null),
    [reachableSections],
  );

  /*
   * Standing in a section narrows the menu to that section (`isGroupVisible`) — but only
   * for somebody who can step back out. A user holding one section and no global scope is
   * never shown a switcher (docs/03 §5), so narrowing would take away their reports with no
   * route back; the same condition `SectionSwitcher` uses to decide whether to render at
   * all decides this.
   */
  const { user } = useAuth();
  const canLeaveSection = canLeaveSectionFor(user?.access);

  const groups = visibleNavigation(checkers, {
    activeSectionId,
    modules,
    sectionSlug,
    sectionSlugs,
    canLeaveSection,
  });

  /**
   * **Opening a vertical's own screen stands you in that vertical.**
   *
   * The four vertical groups also show at group level and in HQ, which is deliberate —
   * a CFO does run all four business lines and HQ assembles their books. What was missing
   * is that the screens behind those rows are *shared*: `/invoices` is the same component
   * under Agency, under Real Estate and under Income, and it reads `activeSectionId` to
   * know whose books it is writing. So AGENCY → Invoices, clicked from HQ, opened a form
   * that filed the document under **Head Office** — with no reason field, because HQ types
   * no invoices — and the row never reached Agency's register or any customer's statement.
   * The menu said Agency and the document said HQ, and nothing on screen said so.
   *
   * Switching here rather than teaching every shared screen a second source of truth: one
   * place, and the answer is then right for the section header, the `X-Section-Id` on every
   * request, the form's `sectionId` and the lists — all of which already read the switcher.
   * It is also *visible*: the switcher updates to Agency, which is the honest description
   * of what just happened, where a hidden per-route override would not be.
   *
   * Only ever to a section the user actually holds — `useSections()` returns exactly those,
   * so an unknown slug is a no-op rather than a switch into a section the API will 403.
   * The cache is cleared for the reason `SectionSwitcher` clears it: every list is
   * section-scoped, and one frame of Agency's invoices under a Real Estate header is worse
   * than a spinner.
   */
  const { data: sections = [] } = useSections();
  const setActiveSection = useUiStore((state) => state.setActiveSection);
  const queryClient = useQueryClient();

  const handleNavigate = (group) => () => {
    const slug = group.sections?.length === 1 ? group.sections[0] : null;
    const target = slug ? sections.find((section) => section.slug === slug) : null;

    if (target && target.id !== activeSectionId) {
      setActiveSection(target.id);
      queryClient.clear();
    }

    onNavigate?.();
  };

  /*
   * The active row is tinted with the *section* colour rather than the neutral accent.
   *
   * Grey-on-grey meant the only thing separating "where I am" from "where the cursor
   * happens to be" was a shade of the same hover colour, and in a nav this long that is a
   * genuine question to have to answer. The section tint answers two at once: which screen
   * is open, and whose books it is writing into.
   */
  const linkClass = ({ isActive }) =>
    cn(
      'group relative flex items-center gap-3 rounded-lg py-2.5 text-sm transition-colors duration-150',
      collapsed ? 'justify-center px-0' : 'px-3',
      isActive
        ? // `bg-section` is the registered token (tailwind.config `colors.section`), so the
          // opacity modifier composes properly. An inline `bg-[hsl(var(--section-active)/0.12)]`
          // looks equivalent and silently compiles to nothing — Tailwind takes the `/` as the
          // opacity separator and drops the declaration.
          'bg-section/10 font-semibold text-foreground'
        : 'text-muted-foreground hover:bg-accent/70 hover:text-foreground',
    );

  /** The active accent bar sits on the leading edge in both directions. */
  const ActiveBar = ({ isActive }) =>
    isActive ? (
      <span
        aria-hidden="true"
        className="absolute inset-y-1.5 start-0 w-[3px] rounded-full"
        style={{ backgroundColor: 'hsl(var(--section-active))' }}
      />
    ) : null;

  /* The icon carries the accent too — at 3px the bar alone is easy to miss at a glance. */
  const iconClass = (isActive) => cn('size-4 shrink-0 transition-colors', isActive && 'text-section');

  return (
    <div className="flex h-full flex-col">
      <div className={cn('p-3', collapsed && 'px-2')} data-tour="section-switcher">
        <SectionSwitcher collapsed={collapsed} />
      </div>

      <Separator />

      <nav
        className="flex-1 space-y-0.5 overflow-y-auto scrollbar-thin p-2.5"
        aria-label={t('groups.dashboard')}
        data-tour="sidebar"
      >
        {groups.map((group) => {
          const isCollapsedGroup = collapsedGroups.includes(group.key);

          // A single-item group renders as a bare link — a disclosure arrow above one
          // row is pure chrome.
          if (group.items.length === 1 && !group.alwaysGroup) {
            const item = group.items[0];
            const Icon = item.icon;
            const label = t(`items.${item.key}`);

            const link = (
              <NavLink to={item.to} end={item.end} className={linkClass} onClick={handleNavigate(group)}>
                {({ isActive }) => (
                  <>
                    <ActiveBar isActive={isActive} />
                    <Icon className={iconClass(isActive)} />
                    {collapsed ? null : <span className="truncate">{label}</span>}
                  </>
                )}
              </NavLink>
            );

            return (
              <div key={group.key} data-nav-group={group.key} className="py-0.5">
                {collapsed ? (
                  <InfoTooltip content={label} side="right">
                    <div>{link}</div>
                  </InfoTooltip>
                ) : (
                  link
                )}
              </div>
            );
          }

          return (
            <Collapsible
              key={group.key}
              /* Two groups legitimately carry a row called "Invoices" — Agency's and Real
                 Estate's — so the group has to be nameable to tell them apart. */
              data-nav-group={group.key}
              open={collapsed ? true : !isCollapsedGroup}
              onOpenChange={() => !collapsed && toggleNavGroup(group.key)}
              className="py-0.5"
            >
              {collapsed ? (
                <Separator className="my-2" />
              ) : (
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="mt-2 flex w-full items-center justify-between rounded-md px-3 py-1.5 text-caption font-semibold uppercase tracking-[0.06em] text-muted-foreground/80 transition-colors hover:text-foreground"
                  >
                    {t(`groups.${group.key}`)}
                    <ChevronDown
                      className={cn(
                        'size-3.5 transition-transform',
                        isCollapsedGroup && '-rotate-90 rtl-flip',
                      )}
                    />
                  </button>
                </CollapsibleTrigger>
              )}

              <CollapsibleContent className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const label = t(`items.${item.key}`);

                  const link = (
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={linkClass}
                      onClick={handleNavigate(group)}
                    >
                      {({ isActive }) => (
                        <>
                          <ActiveBar isActive={isActive} />
                          <Icon className={iconClass(isActive)} />
                          {collapsed ? null : <span className="truncate">{label}</span>}
                        </>
                      )}
                    </NavLink>
                  );

                  return collapsed ? (
                    <InfoTooltip key={item.key} content={label} side="right">
                      <div>{link}</div>
                    </InfoTooltip>
                  ) : (
                    <div key={item.key}>{link}</div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </nav>

      <Separator />
      <div
        className={cn(
          'flex items-center gap-2 p-3 text-caption text-muted-foreground',
          collapsed && 'justify-center px-2',
        )}
      >
        {collapsed ? null : (
          <span className="truncate">{t('common:sidebar.version', { version: APP_VERSION })}</span>
        )}
      </div>
    </div>
  );
};

/** The desktop rail. Below `lg` the shell renders `SidebarContent` inside a Sheet. */
export const Sidebar = () => {
  const { t } = useTranslation('common');
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);

  return (
    <aside
      className={cn(
        'relative hidden shrink-0 border-e bg-card transition-[width] duration-200 lg:flex lg:flex-col',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <SidebarContent />

      <Button
        variant="ghost"
        size="icon-sm"
        onClick={toggleSidebar}
        aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        className="absolute -end-3.5 top-4 z-10 rounded-full border bg-background shadow-sm"
      >
        {collapsed ? (
          <PanelLeftOpen className="size-3.5 rtl-flip" />
        ) : (
          <PanelLeftClose className="size-3.5 rtl-flip" />
        )}
      </Button>
    </aside>
  );
};

export default Sidebar;
